import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Seo from '../seo/Seo';
import { CTA_LABEL, CTA_SUBLINE, bookingUrl } from '../site';

interface Destination {
  title: string;
  detail: string;
  to?: string;
  href?: string;
}

const destinations: Destination[] = [
  {
    title: 'What we build',
    detail: 'AI agents and agentic workflows, AFK Development, and ten years of AWS.',
    to: '/',
  },
  {
    title: 'AFK Development',
    detail: 'Away-from-keyboard development: the pipeline, stage by stage, and what it refuses to do.',
    to: '/afk-development',
  },
  {
    title: 'Case studies',
    detail: 'Three engagements, with the numbers and the constraints that produced them.',
    to: '/#projects',
  },
  {
    title: CTA_LABEL,
    detail: CTA_SUBLINE,
    href: bookingUrl('404'),
  },
];

const NotFoundPage = () => (
  <div className="bg-gray-900">
    <Seo
      title="Page not found | Kult.io"
      description="The link is either old or wrong. Here is where to go instead."
      noindex
    />

    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 md:py-32">
      <div className="max-w-measure">
        <h1 className="font-display text-display-md font-bold text-white sm:text-display-lg">
          That page is not here.
        </h1>
        <p className="mt-6 text-lg text-gray-300">
          The link is either old or wrong. Nothing is broken on your end. Here is where to go
          instead.
        </p>
      </div>

      <ul className="mt-14 border-t border-gray-800">
        {destinations.map((destination) => {
          const content = (
            <>
              <span className="font-display text-display-sm font-medium text-white transition-colors duration-150 group-hover:text-accent-400">
                {destination.title}
              </span>
              <span className="mt-2 block max-w-measure text-gray-400 sm:mt-0">
                {destination.detail}
              </span>
              <ArrowRight
                aria-hidden="true"
                className="mt-4 h-5 w-5 flex-shrink-0 text-gray-500 transition-colors duration-150 group-hover:text-accent-400 sm:mt-1"
              />
            </>
          );
          const rowClass =
            'group flex min-h-[44px] flex-col gap-x-10 border-b border-gray-800 py-6 sm:flex-row sm:items-baseline sm:justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400';

          return (
            <li key={destination.title}>
              {destination.to ? (
                <Link to={destination.to} className={rowClass}>
                  {content}
                </Link>
              ) : (
                <a
                  href={destination.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={rowClass}
                >
                  {content}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
);

export default NotFoundPage;
